import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import { normalizeCurrency } from '@/lib/currency';
import {
  propertyTypeMeta, bookableOptions, computeTotal, nightsBetween, formatMoney, todayISO, addDaysISO,
  type HebergementProperty, type HebergementUnit,
} from '@/lib/hebergement';

interface MediaRow { id: number; url: string; type: string; caption: string | null; is_cover: boolean; sort_order: number }
interface AmenityRow { id: number; name: string; icon: string | null }

export default function DetailHebergementPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant, isTenant, loading: tenantLoading } = useTenant();
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  const [property, setProperty] = useState<HebergementProperty | null>(null);
  const [units, setUnits] = useState<HebergementUnit[]>([]);
  const [media, setMedia] = useState<MediaRow[]>([]);
  const [amenities, setAmenities] = useState<AmenityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMedia, setActiveMedia] = useState(0);
  // Lightbox plein écran (clic sur l'image de la galerie)
  const [lightbox, setLightbox] = useState<number | null>(null);

  // Suivi de visite de la fiche hébergement publique (vues, IP, géo, provenance).
  useThemePageTracking('hebergement', tenant?.id ?? null, isTenant && !!tenant && !!property);

  // Formulaire de réservation
  const [optionValue, setOptionValue] = useState('property');
  const [checkIn, setCheckIn] = useState(todayISO());
  const [checkOut, setCheckOut] = useState(addDaysISO(todayISO(), 2));
  const [guests, setGuests] = useState('2');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [availability, setAvailability] = useState<'unknown' | 'checking' | 'available' | 'unavailable'>('unknown');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmation, setConfirmation] = useState<number | null>(null);

  useEffect(() => {
    if (!id) { setError('Hébergement introuvable'); setLoading(false); return; }
    // Attente de la résolution du tenant : on ne charge un bien qu'une fois
    // le commerce courant connu, puis on filtre STRICTEMENT dessus
    // (isolation par commerce : aucun bien d'un autre marchand).
    if (tenantLoading) return;
    if (!isTenant || !tenant) { setError('Hébergement introuvable'); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data: prop, error: propErr } = await supabase
          .from('hebergement_properties')
          .select('*')
          .eq('id', Number(id))
          .eq('merchant_id', tenant.id)
          .eq('published', true)
          .maybeSingle();
        if (propErr) throw propErr;
        if (!prop) { if (!cancelled) { setError('Hébergement introuvable ou non publié'); setLoading(false); } return; }
        if (cancelled) return;
        setProperty(prop as HebergementProperty);

        const [unitRes, mediaRes, amenityRes] = await Promise.all([
          supabase.from('hebergement_units').select('*').eq('property_id', Number(id)).eq('published', true),
          supabase.from('hebergement_media').select('*').eq('property_id', Number(id)).order('sort_order', { ascending: true }),
          supabase.from('hebergement_amenities').select('*').eq('property_id', Number(id)).order('name', { ascending: true }),
        ]);
        if (cancelled) return;
        setUnits((unitRes.data || []) as HebergementUnit[]);
        setMedia((mediaRes.data || []) as MediaRow[]);
        setAmenities((amenityRes.data || []) as AmenityRow[]);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isTenant, tenant, tenantLoading]);

  useEffect(() => {
    if (property) document.title = property.name;
  }, [property]);

  // Navigation clavier de la lightbox (Échap pour fermer, flèches pour défiler)
  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') setLightbox((i) => (i == null ? i : (i - 1 + Math.max(1, media.length)) % Math.max(1, media.length)));
      if (e.key === 'ArrowRight') setLightbox((i) => (i == null ? i : (i + 1) % Math.max(1, media.length)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, media.length]);

  const options = useMemo(
    () => (property ? bookableOptions(property, units) : []),
    [property, units],
  );

  useEffect(() => {
    if (options.length > 0) setOptionValue(options[0].value);
  }, [options]);

  const selectedOption = options.find((o) => o.value === optionValue) || options[0] || null;
  const unitId = selectedOption?.unitId ?? null;
  const pricePerNight = selectedOption?.price ?? (Number(property?.price) || 0);
  const nights = nightsBetween(checkIn, checkOut);
  const total = computeTotal(pricePerNight, checkIn, checkOut);

  // Vérifie la disponibilité réelle (moteur serveur) dès que les paramètres changent.
  useEffect(() => {
    if (!property || !checkIn || !checkOut || checkOut <= checkIn) { setAvailability('unknown'); return; }
    let cancelled = false;
    setAvailability('checking');
    const timer = setTimeout(async () => {
      try {
        const { data } = await supabase.rpc('hebergement_check_availability', {
          p_property_id: property.id,
          p_unit_id: unitId,
          p_check_in: checkIn,
          p_check_out: checkOut,
        });
        if (!cancelled) setAvailability(data === true ? 'available' : 'unavailable');
      } catch {
        if (!cancelled) setAvailability('unknown');
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [property, unitId, checkIn, checkOut]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!property || !tenant) return;
    if (!checkIn || !checkOut || checkOut <= checkIn) { setFormError('Dates invalides : le départ doit suivre l\u2019arrivée.'); return; }
    if (!name.trim()) { setFormError('Votre nom est obligatoire.'); return; }
    if (availability === 'unavailable') { setFormError('Ce séjour n\u2019est pas disponible sur ces dates.'); return; }
    setSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('hebergement_create_reservation', {
        p_merchant_id: tenant.id,
        p_property_id: property.id,
        p_unit_id: unitId,
        p_customer_id: null,
        p_guest_name: name.trim(),
        p_guest_email: email.trim() || null,
        p_guest_phone: phone.trim() || null,
        p_check_in: checkIn,
        p_check_out: checkOut,
        p_guests: Math.max(1, parseInt(guests, 10) || 1),
        p_price_per_night: pricePerNight,
        p_discount: 0,
        p_fees: 0,
        p_currency: currency,
        p_source: 'zifek_website',
        p_notes: null,
      });
      if (rpcErr) {
        if (/indisponible/i.test(rpcErr.message)) throw new Error('Ce séjour vient d\u2019être réservé sur ces dates. Essayez d\u2019autres dates.');
        throw rpcErr;
      }
      setConfirmation(data as number);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Erreur lors de la réservation.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-hotel-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Hébergement introuvable</h2>
        <p className="text-sm text-foreground-500 mb-6">Ce bien n&apos;existe pas ou n&apos;est plus publié.</p>
        <Link to="/hebergements" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
          Voir les hébergements
        </Link>
      </div>
    );
  }

  const meta = propertyTypeMeta(property.type);
  // Diaporama : on garde l'ordre du marchand et on retombe sur l'image de
  // couverture s'il n'y a aucune photo dans la galerie.
  const slides: MediaRow[] = media.length > 0
    ? media
    : property.cover_image
      ? [{ id: -1, url: property.cover_image, type: 'image', caption: null, is_cover: true, sort_order: 0 }]
      : [];
  const slideIndex = Math.min(activeMedia, Math.max(0, slides.length - 1));
  const current = slides[slideIndex];
  const goPrev = () => setActiveMedia((i) => (slides.length ? (i - 1 + slides.length) % slides.length : 0));
  const goNext = () => setActiveMedia((i) => (slides.length ? (i + 1) % slides.length : 0));

  const lightboxIndex = lightbox != null ? Math.min(lightbox, Math.max(0, slides.length - 1)) : null;
  const lightboxSlide = lightboxIndex != null ? slides[lightboxIndex] : null;
  const closeLightbox = () => setLightbox(null);
  const lightboxPrev = () => setLightbox((i) => (i == null || !slides.length ? i : (i - 1 + slides.length) % slides.length));
  const lightboxNext = () => setLightbox((i) => (i == null || !slides.length ? i : (i + 1) % slides.length));

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/hebergements" className="hover:text-foreground-700 no-underline whitespace-nowrap">Hébergements</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[200px]">{property.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne gauche : galerie + infos */}
          <div className="lg:col-span-2">
            {/* Galerie — diaporama horizontal, images entièrement visibles */}
            <div className="rounded-xl overflow-hidden border border-background-200/70 bg-background-100">
              <div className="relative h-[260px] sm:h-[340px] lg:h-[420px] flex items-center justify-center bg-background-100">
                {current ? (
                  current.type === 'video' ? (
                    <video src={current.url} controls className="max-h-full max-w-full object-contain bg-black" />
                  ) : (
                    <img
                      src={current.url}
                      alt={current.caption || property.name}
                      title={`${property.name} — ${meta.label}`}
                      onClick={() => setLightbox(slideIndex)}
                      className="max-h-full max-w-full object-contain cursor-zoom-in"
                    />
                  )
                ) : (
                  <div className="flex flex-col items-center gap-2 text-foreground-300">
                    <i className="ri-image-line text-5xl"></i>
                    <span className="text-xs text-foreground-400">Aucune photo pour ce bien</span>
                  </div>
                )}

                {slides.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrev}
                      aria-label="Image précédente"
                      className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/70 flex items-center justify-center text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      aria-label="Image suivante"
                      className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/70 flex items-center justify-center text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                    <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-foreground-950/60 text-background-50 text-[11px] font-medium">
                      {slideIndex + 1} / {slides.length}
                    </span>
                  </>
                )}

                {current && current.type !== 'video' && (
                  <button
                    type="button"
                    onClick={() => setLightbox(slideIndex)}
                    aria-label="Afficher en plein écran"
                    className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/70 text-foreground-700 text-[11px] font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-fullscreen-line"></i>
                    Plein écran
                  </button>
                )}
              </div>
              {current?.caption && (
                <div className="px-4 py-2 border-t border-background-200/70 bg-background-50">
                  <p className="text-xs text-foreground-500 truncate">{current.caption}</p>
                </div>
              )}
            </div>

            {slides.length > 1 && (
              <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
                {slides.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setActiveMedia(i)}
                    className={`relative w-[84px] h-[60px] flex-shrink-0 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                      slideIndex === i ? 'border-primary-500' : 'border-background-200/70 hover:border-background-300/60'
                    }`}
                  >
                    {m.type === 'video' ? (
                      <span className="w-full h-full flex items-center justify-center bg-background-100 text-foreground-500">
                        <i className="ri-play-circle-line text-xl"></i>
                      </span>
                    ) : (
                      <img src={m.url} alt={m.caption || `${property.name} ${i + 1}`} className="w-full h-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* En-tête */}
            <div className="mt-6">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-3">
                <i className={meta.icon}></i>{meta.label}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">{property.name}</h1>
              <p className="text-sm text-foreground-500 mb-4">
                <i className="ri-map-pin-line mr-1 text-primary-500"></i>
                {[property.address, property.city, property.region, property.country].filter(Boolean).join(', ') || 'Localisation non renseignée'}
              </p>
              <div className="flex flex-wrap gap-2 text-xs text-foreground-600">
                <span className="px-3 py-1 rounded-full bg-background-100 flex items-center gap-1.5"><i className="ri-user-line"></i>{property.capacity} voyageurs</span>
                <span className="px-3 py-1 rounded-full bg-background-100 flex items-center gap-1.5"><i className="ri-hotel-bed-line"></i>{property.bed_count} lits</span>
                <span className="px-3 py-1 rounded-full bg-background-100 flex items-center gap-1.5"><i className="ri-drop-line"></i>{property.bathroom_count} salle(s) de bain</span>
              </div>
            </div>

            {property.description && (
              <div className="mt-6">
                <h2 className="text-sm font-bold text-foreground-950 mb-2">Description</h2>
                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{property.description}</p>
              </div>
            )}

            {amenities.length > 0 && (
              <div className="mt-6">
                <h2 className="text-sm font-bold text-foreground-950 mb-3">Équipements &amp; services</h2>
                <div className="flex flex-wrap gap-2">
                  {amenities.map((a) => (
                    <span key={a.id} className="px-3 py-1.5 rounded-full bg-accent-50 text-accent-800 text-xs font-medium flex items-center gap-1.5">
                      <i className={a.icon || 'ri-checkbox-circle-line'}></i>{a.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite : réservation */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 bg-background-50 border border-background-200/70 rounded-xl p-5">
              {confirmation != null ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-1">Demande envoyée !</h3>
                  <p className="text-sm text-foreground-500 mb-1">
                    Réservation n°<span className="font-semibold text-foreground-800">{confirmation}</span>
                  </p>
                  <p className="text-xs text-foreground-400">
                    {formatMoney(total, currency)} · du {checkIn} au {checkOut}
                  </p>
                  <p className="text-xs text-foreground-500 mt-3">Le marchand vous contactera pour confirmer votre séjour.</p>
                  <Link to="/hebergements" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
                    <i className="ri-arrow-left-line"></i>Voir d&apos;autres hébergements
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <div className="flex items-end justify-between mb-4">
                    <div>
                      <span className="text-xl font-bold font-heading text-primary-600">{pricePerNight.toLocaleString()} {currency}</span>
                      <span className="text-xs text-foreground-400"> / nuit</span>
                    </div>
                  </div>

                  {options.length > 1 && (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Je réserve</label>
                      <select value={optionValue} onChange={(e) => setOptionValue(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer">
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label} — {o.price.toLocaleString()} {currency}/nuit</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Arrivée</label>
                      <input type="date" name="checkin" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} required
                        className="w-full px-2.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-950 focus:outline-none focus:border-primary-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Départ</label>
                      <input type="date" name="checkout" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} required
                        className="w-full px-2.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-950 focus:outline-none focus:border-primary-300" />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Voyageurs</label>
                    <input type="number" name="guests" min={1} max={property.capacity || 20} value={guests} onChange={(e) => setGuests(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                  </div>

                  <div className="space-y-3 mb-3">
                    <input type="text" name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom complet *" required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                    <input type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                    <input type="tel" name="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                  </div>

                  {/* Dispo */}
                  <div className={`px-3 py-2.5 rounded-md flex items-center gap-2 text-xs mb-3 ${
                    availability === 'available' ? 'bg-accent-50 text-accent-700'
                    : availability === 'unavailable' ? 'bg-red-50 text-red-600'
                    : 'bg-background-100 text-foreground-500'
                  }`}>
                    <i className={availability === 'checking' ? 'ri-loader-4-line animate-spin' : availability === 'available' ? 'ri-checkbox-circle-line' : availability === 'unavailable' ? 'ri-close-circle-line' : 'ri-information-line'}></i>
                    {availability === 'checking' ? 'Vérification…'
                      : availability === 'available' ? 'Disponible sur ces dates'
                      : availability === 'unavailable' ? 'Indisponible sur ces dates'
                      : 'Sélectionnez des dates'}
                  </div>

                  {nights > 0 && (
                    <div className="flex items-center justify-between text-sm py-2 mb-2 border-t border-background-200/70">
                      <span className="text-foreground-500 text-xs">{nights} nuit{nights > 1 ? 's' : ''} × {pricePerNight.toLocaleString()} {currency}</span>
                      <span className="font-bold font-heading text-foreground-950">{formatMoney(total, currency)}</span>
                    </div>
                  )}

                  {formError && (
                    <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2 mb-3">
                      <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                      <span className="text-xs text-red-700">{formError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || availability === 'unavailable'}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? <><i className="ri-loader-4-line animate-spin"></i>Envoi…</> : <><i className="ri-calendar-check-line"></i>Demander la réservation</>}
                  </button>
                  <p className="text-[11px] text-foreground-400 text-center mt-3">
                    Aucun paiement à cette étape. Le marchand confirme votre séjour.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox plein écran */}
      {lightboxSlide && lightboxIndex != null && (
        <div className="fixed inset-0 z-[95] bg-foreground-950/95 flex flex-col">
          <div className="flex items-center justify-between px-4 md:px-6 h-14 flex-shrink-0">
            <span className="text-xs text-background-50/80 truncate">
              {property.name} · {lightboxIndex + 1} / {slides.length}
            </span>
            <button
              type="button"
              onClick={closeLightbox}
              aria-label="Fermer"
              className="w-9 h-9 rounded-full bg-background-50/15 hover:bg-background-50/25 flex items-center justify-center text-background-50 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <div className="flex-1 relative flex items-center justify-center px-4 pb-4">
            {lightboxSlide.type === 'video' ? (
              <video src={lightboxSlide.url} controls autoPlay className="max-h-full max-w-full object-contain" />
            ) : (
              <img
                src={lightboxSlide.url}
                alt={lightboxSlide.caption || property.name}
                className="max-h-full max-w-full object-contain"
              />
            )}
            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={lightboxPrev}
                  aria-label="Image précédente"
                  className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/25 flex items-center justify-center text-background-50 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button
                  type="button"
                  onClick={lightboxNext}
                  aria-label="Image suivante"
                  className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/25 flex items-center justify-center text-background-50 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </>
            )}
            {lightboxSlide.caption && (
              <p className="absolute bottom-2 left-0 right-0 text-center text-xs text-background-50/80 px-6 truncate">
                {lightboxSlide.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}